import { useState, useRef, useEffect } from "react";
import useAxios from "axios-hooks";
import axios from "axios";
import { parseErrorString } from "../ValidationErrorsModal";

/**
 * Custom hook for handling on/off settings form submission with proper
 * cancel handling, error parsing, and mount state tracking.
 * 
 * @param {string} checklistId - The checklist ID
 * @returns {object} Hook state and handlers
 */
export const useOnOffSettingsSubmit = (checklistId) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const isMountedRef = useRef(true);

  // Track component mount state to avoid setting state after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [{ data: onOffSetting, loading: loadingOnOffSetting }] = useAxios(
    `/checklist/${checklistId}/on-off-settings`
  );

  const [{ data: cobrandsData }] = useAxios(`/internal/cobrands`);

  const cobrandOptions = cobrandsData?.map((cobrand) => ({
    value: cobrand.cobrandId,
    label: cobrand.name,
  })) || [];

  const [{ loading }, postForm] = useAxios(
    {
      url: `/checklist/${checklistId}/assign`,
      method: "post",
    },
    { manual: true }
  );

  /**
   * Maps form values to the API submit format
   */
  const mapValuesForSubmit = (values) => {
    const isGlobalOn = values.globalOn;
    const clients = (values?.client || []).map((c) => ({
      client: {
        id: c,
        onByDefault: !isGlobalOn,
      },
    }));
    const cobrands = (values?.cobrand || []).map((c) => ({
      cobrand: {
        id: c,
        onByDefault: !isGlobalOn,
      },
    }));
    const cobrandTags = (values?.cobrandTag || []).map((c) => ({
      cobrandTag: {
        id: c,
        onByDefault: !isGlobalOn,
      },
    }));

    return [
      ...clients,
      ...cobrands,
      ...cobrandTags,
      {
        globalChecklist: {
          id: checklistId,
          onByDefault: values.globalOn,
        },
      },
    ];
  };

  /**
   * Clears all error and success states before a new submission
   */
  const clearState = () => {
    setErrorMessage(null);
    setValidationErrors([]);
    setSuccessMessage(null);
  };

  /**
   * Handles the form submission with proper error handling
   * @param {object} values - Form values
   * @param {object} options - Optional callbacks { onSuccess }
   * @returns {Promise<boolean>} - True if successful, false otherwise
   */
  const handleSubmit = async (values, options = {}) => {
    const { onSuccess } = options;

    // Guard against double submission
    if (loading) {
      console.warn("Submission already in progress, ignoring");
      return false;
    }

    clearState();

    try {
      const submitData = mapValuesForSubmit(values);
      console.log("Submitting on/off settings:", JSON.stringify(submitData, null, 2));
      await postForm({ data: submitData });

      // Only update state if still mounted
      if (isMountedRef.current) {
        setSuccessMessage("Settings saved successfully!");
        onSuccess?.();
      }
      return true;
    } catch (e) {
      // Ignore cancelled requests (happens on unmount or tab switch)
      if (axios.isCancel(e) || e?.constructor?.name === "Cancel" || e?.__CANCEL__) {
        console.log("Request was cancelled (likely component unmounted)");
        return false;
      }

      // Ignore if component is unmounted
      if (!isMountedRef.current) {
        console.log("Component unmounted, ignoring error");
        return false;
      }

      // Check for network errors (no response from server)
      if (e.message === "Network Error" && !e.response) {
        console.error("Network error - request may have been aborted or server unreachable");
        console.error("Error details:", e.code, e.config?.url);
        setErrorMessage("Network error occurred. Please check your connection and try again.");
        return false;
      }

      console.error("Failed to save on/off settings:", e);
      console.error("Response status:", e.response?.status);
      console.error("Response data:", e.response?.data);
      const responseData = e.response?.data;

      // Check for string-based error messages (existing format)
      const errorMsg =
        responseData?.errors?.[0]?.errorMessage ||
        responseData?.errors?.[0]?.ErrorMessage ||
        (typeof responseData === "string" ? responseData : null);

      if (
        errorMsg &&
        (errorMsg.includes("Proprietary general Amount") ||
          errorMsg.includes("specific course does not exist") ||
          errorMsg.includes("Cobrand="))
      ) {
        const parsed = parseErrorString(errorMsg);
        if (parsed.length > 0) {
          setValidationErrors(parsed);
          setShowValidationModal(true);
          return false;
        }
      }

      // Generic error - show in message bar
      setErrorMessage(errorMsg || e.message || "An unexpected error occurred.");
      return false;
    }
  };

  /**
   * Handles excluding cobrands from assignment (called from ValidationErrorsModal)
   * @param {number[]} cobrandIds - Array of cobrand IDs to exclude
   * @param {object} values - Current form values
   * @param {function} setFieldValue - Formik setFieldValue
   */
  const handleExcludeCobrands = (cobrandIds, values, setFieldValue) => {
    if (values.globalOn) {
      // Add to exclude list (cobrand field when globalOn is true)
      const currentCobrands = values.cobrand || [];
      const newCobrands = [...new Set([...currentCobrands, ...cobrandIds])];
      setFieldValue("cobrand", newCobrands);
    } else {
      // Remove from include list
      const currentCobrands = values.cobrand || [];
      const newCobrands = currentCobrands.filter((id) => !cobrandIds.includes(id));
      setFieldValue("cobrand", newCobrands);
    }
    setShowValidationModal(false);
    setValidationErrors([]);
  };

  return {
    // State
    onOffSetting,
    loadingOnOffSetting,
    loading,
    errorMessage,
    setErrorMessage,
    successMessage,
    setSuccessMessage,
    validationErrors,
    showValidationModal,
    setShowValidationModal,
    cobrandOptions,
    
    // Actions
    handleSubmit,
    handleExcludeCobrands,
    clearState,
  };
};

export default useOnOffSettingsSubmit;
